import React, { useMemo, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Grid, CircularProgress, Box } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ShareIcon from "@mui/icons-material/Share";
import CategoreyCard from "../../components/CategoreyCard";
import BottomNav from "../../components/Bottom nav/BottomNav";
import BookmarkNotFound from "../myListing/bookmark/bookmarkNotFound/BookmarkNotFound";
import useCommonQuery from "../../store/common/useCommonQuery";
import LayoutToggle from "../../components/LayoutToggle/LayoutToggle";
import { variableContext } from "../../context/VariableContex";
import "./ListView.css";
import "../../page-header-controls.css";

// Exact display order matching the Home Page category buttons
const CATEGORY_ORDER = [
  "Hello Doctor",
  "Public Service",
  "Heavy Equipment",
  "Building Service",
  "Building Material Service",
  "Ambulance & Auto Service",
];

function AllSubCategories() {
  const history = useHistory();
  const [search, setSearch] = useState("");
  const { layoutMode } = useContext(variableContext);

  const handleShare = async () => {
    try { await navigator.share({ url: window.location.href }); } catch (_) {}
  };

  const { data: categoryData, isLoading: categoriesLoading } =
    useCommonQuery.Get_category({ limit: 200, offset: 0 });

  const { data: subcategoryData, isLoading: subcategoriesLoading } =
    useCommonQuery.Get_subcategory({ limit: 2000, offset: 0 });

  const isLoading = categoriesLoading || subcategoriesLoading;

  const groupedSections = useMemo(() => {
    const categories = categoryData?.data?.results ?? [];
    const subcategories = subcategoryData?.data?.results ?? [];

    if (categories.length === 0 || subcategories.length === 0) return [];

    const lowerSearch = search.trim().toLowerCase();

    // Build map: category_id (as string and number) → subcategories[]
    const subMap = {};
    subcategories.forEach((sub) => {
      // Normalise key to string to avoid type mismatch
      const key = String(sub.category);
      if (!subMap[key]) subMap[key] = [];
      subMap[key].push(sub);
    });

    // Sort each bucket by priority ascending
    Object.values(subMap).forEach((arr) =>
      arr.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    );

    // Sort categories: CATEGORY_ORDER first, then DB priority for the rest
    const sorted = [...categories].sort((a, b) => {
      const ai = CATEGORY_ORDER.findIndex(
        (n) => n.toLowerCase() === (a.name ?? "").toLowerCase()
      );
      const bi = CATEGORY_ORDER.findIndex(
        (n) => n.toLowerCase() === (b.name ?? "").toLowerCase()
      );
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return (a.priority ?? 0) - (b.priority ?? 0);
    });

    // Build sections
    return sorted.reduce((acc, cat) => {
      const subs = subMap[String(cat.id)] ?? [];
      const filtered = lowerSearch
        ? subs.filter(
            (sub) =>
              (sub.name ?? "").toLowerCase().includes(lowerSearch) ||
              (cat.name ?? "").toLowerCase().includes(lowerSearch)
          )
        : subs;
      if (filtered.length > 0) {
        acc.push({ category: cat, subcategories: filtered });
      }
      return acc;
    }, []);
  }, [categoryData, subcategoryData, search]);

  const handleClick = (subCatId) => {
    history.push(`/category//subCategory/${subCatId}`);
  };

  return (
    <div className={`asc-page${layoutMode === "list" ? " asc-list" : ""}`} style={{ height: "100vh", overflowY: "scroll", backgroundColor: "#f5f6fa" }}>

      {/* ── Sticky header: [Home] [Search] [LayoutToggle] [Share] ── */}
      <div className="phc-bar">
        <div className="phc-inner">
          <div className="phc-row">

            {/* Home button */}
            <button
              className="phc-back"
              onClick={() => history.push("/")}
              style={{ gap: 4 }}
            >
              <HomeIcon className="phc-back-icon" />
              <span className="phc-back-label">Home</span>
            </button>

            {/* Left spacer — pushes search to center */}
            <div className="phc-spacer" />

            {/* Search bar */}
            <div className="phc-search-wrap">
              <input
                className="phc-search-input"
                type="text"
                placeholder="Search subcategories..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button className="phc-search-btn">Search</button>
            </div>

            {/* Right spacer — keeps search centered */}
            <div className="phc-spacer" />

            {/* Layout Switch — same component as Home Page */}
            <LayoutToggle size="sm" />

            {/* Share button */}
            <button className="phc-share-btn" onClick={handleShare}>
              <ShareIcon />
            </button>

          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="clv-body" style={{ paddingBottom: "5rem" }}>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && groupedSections.length === 0 && (
          <BookmarkNotFound title="No Subcategories Found" />
        )}

        {!isLoading &&
          groupedSections.map(({ category, subcategories }, sectionIndex) => (
            <div key={category.id} style={{ marginBottom: "0.5rem" }}>

              {/* ── Category section header ── */}
              <div
                className={`asc-section-header${layoutMode !== "list" ? " asc-section-header--grid" : ""}`}
                style={{
                marginTop: sectionIndex === 0 ? "0.5rem" : "1.25rem",
                marginLeft: "0",
                marginRight: "0",
              }}>
                <span className="asc-header-name">
                  {category.name}
                </span>
                <span className="asc-header-count">
                  {subcategories.length} {subcategories.length === 1 ? "item" : "items"}
                </span>
              </div>

              {/* ── Subcategory cards ── */}
              <div className="asc-card-wrap" style={{ backgroundColor: "#ffffff", padding: "0.5rem 0.75rem 0.75rem" }}>
                <Grid container spacing={1.5}>
                  {subcategories.map((sub) => (
                    <CategoreyCard
                      key={sub.id}
                      id={sub.id}
                      title={sub.name}
                      image={sub.image}
                      handleClick={handleClick}
                    />
                  ))}
                </Grid>
              </div>

            </div>
          ))}
      </div>

      <BottomNav />
    </div>
  );
}

export default React.memo(AllSubCategories);
